/**
 * usePendingBadge
 * Abonnement global temps réel aux 3 collections de demandes en attente.
 * Met à jour pendingBadgeCount dans uiStore — appelé une seule fois dans App.tsx.
 */

import { useEffect, useRef } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { useAuthStore, useResidenceStore, useUIStore } from '@/stores';

export const usePendingBadge = () => {
  const residenceId = useResidenceStore(state => state.currentResidence?.id);
  const userCategory = useAuthStore(state => state.currentUser?.category);
  const setPendingBadgeCount = useUIStore(state => state.setPendingBadgeCount);

  // Seulement pour les managers — garantit que le token auth est propagé aux règles Firestore
  const isManager = ['admin', 'council', 'gardien'].includes(userCategory || '');

  // Compteurs stables hors React — ne sont jamais remis à 0 par un re-render
  const counts = useRef({ members: 0, category: 0, building: 0 });

  useEffect(() => {
    if (!residenceId || !isManager) return;

    const update = () => {
      setPendingBadgeCount(counts.current.members + counts.current.category + counts.current.building);
    };

    const unsubMembers = onSnapshot(
      query(collection(db, 'members'), where('residenceId', '==', residenceId), where('status', '==', 'pending')),
      s => { counts.current.members = s.size; update(); },
      () => {}
    );
    const unsubCategory = onSnapshot(
      query(collection(db, `residences/${residenceId}/categoryChangeRequests`), where('status', '==', 'pending')),
      s => { counts.current.category = s.size; update(); },
      () => {}
    );
    const unsubBuilding = onSnapshot(
      query(collection(db, `residences/${residenceId}/buildingChangeRequests`), where('status', '==', 'pending')),
      s => { counts.current.building = s.size; update(); },
      () => {}
    );

    return () => {
      unsubMembers();
      unsubCategory();
      unsubBuilding();
    };
  }, [residenceId, isManager, setPendingBadgeCount]);
};
